import { BodyContentDetails } from '@modules/front-office/interface/voucher.interface';
import {
  Document,
  Image,
  Page,
  PDFViewer,
  Text,
  View,
} from '@react-pdf/renderer';
import { PageSize, Style } from '@react-pdf/types';
import parse, { DOMNode, Element } from 'html-react-parser';
import { ReactNode, useEffect, useState } from 'react';
import {
  BorderType,
  Cell,
  EdgeInsets,
  Field,
  FontWeightType,
  HeaderFooterType,
  Row,
  Section,
  SharedCellStyle,
  TableCell,
  TableField,
  TemplateState,
  TypographyType,
} from './react-pdf.interface';
import {
  imagePlaceholder,
  isAllSectionsEmpty,
  isBodyContentDetailsArray,
  isTemplateState,
  safeJSONParse,
} from './utils/react-pdf.helper';

type HtmlRenderContext = {
  rowIndex?: number;
  totalRowCount?: number;
  cellIndex?: number;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  listStack?: number[];
  isUnOrderedList?: boolean;
};

type PdfBorderStyle = {
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  borderStyle?: 'solid' | 'dashed' | 'dotted';
  borderColor?: string;
};

type HtmlAttribs = {
  style?: string;
  [key: string]: string | undefined;
};

const getListMarker = (
  level: number,
  index: number,
  isUnOrderedList?: boolean,
): string => {
  if (!index) return '•';

  // decimal
  if ([1, 4, 7, 10].includes(level)) {
    if (isUnOrderedList) return `•`;
    return `${index}.`;
  }

  // a, b, c
  if ([2, 5, 8, 11].includes(level)) {
    if (isUnOrderedList) return `•`;

    const char = String.fromCharCode(96 + index);
    return `${char}.`;
  }

  //  Roman numerals
  if ([3, 6, 9, 12].includes(level)) {
    if (isUnOrderedList) return `•`;

    const roman = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
    return `${roman[index - 1] || index}.`;
  }

  // fallback → numbers
  return `${index}.`;
};

const renderList = (content: BodyContentDetails[]) => (
  <>
    {content
      ?.sort((a, b) => a.order - b.order)
      ?.map((item, index) => (
        <View key={index}>
          <Text
            style={{
              fontWeight: item?.titleFontWeight || 700,
              marginBottom: 3,
              marginTop: 5,
              fontSize: 9,
            }}
          >
            {item.title}
          </Text>

          {item?.text?.map((textItem, textIndex) => (
            <Text
              key={textIndex}
              style={{
                fontWeight: item?.textFontWeight || 300,
                textAlign: 'justify',
                marginBottom: 3,
                fontSize: 8,
              }}
            >
              {`${
                item?.orderStyle === 'Ordered' || !item?.orderStyle
                  ? `${textIndex + 1}. `
                  : '• '
              }${textItem}`}
            </Text>
          ))}
        </View>
      ))}
  </>
);

const getShowAllPageHeight = (
  footer: HeaderFooterType[] | undefined,
  heights: number[] = [],
): number => {
  if (!footer) return 0;

  return footer.reduce((total, section, i) => {
    return section.showAllPage ? total + (heights[i] ?? 0) : total;
  }, 0);
};

const toPdfUnit = (value?: number | string): number | undefined => {
  if (typeof value === 'number') return value * 0.75;

  if (typeof value === 'string') {
    // Allow decimals
    if (/^\d+(\.\d+)?%$/.test(value)) return parseFloat(value) * 0.75;
    if (/^\d+(\.\d+)?px$/.test(value)) return parseFloat(value) * 0.75;
    if (/^\d+(\.\d+)?pt$/.test(value)) return parseFloat(value);
    if (/^\d+(\.\d+)?in$/.test(value)) return parseFloat(value) * 72;
    if (/^\d+(\.\d+)?$/.test(value)) return parseFloat(value) * 0.75;
  }

  return undefined;
};

const toPercent = (val?: number | string) =>
  val === 'auto' || val === 'fit-content' || val === undefined
    ? undefined
    : typeof val === 'number'
      ? `${val}%`
      : val;

const parseStyleString = (styleStr = ''): Record<string, string> =>
  Object.fromEntries(
    styleStr
      .split(';')
      .map((s) => {
        const [prop, val] = s.split(':').map((str) => str.trim());
        return [prop, val];
      })
      .filter(Boolean),
  );

const measurePdfHeight = (renderFooterView: ReactNode): Promise<number> => {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '595px';
    container.style.height = 'auto';
    container.style.visibility = 'hidden';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    const TempFooterDocument = () => (
      <PDFViewer showToolbar={false}>
        <Document
          onRender={(props) => {
            try {
              const layoutData =
                props['_INTERNAL__LAYOUT__DATA_']?.children?.[0]?.children?.[0];
              const height = layoutData?.box?.height;

              if (height != null && height !== 0) {
                setTimeout(() => {
                  container.remove();
                  resolve(height);
                }, 0);
              } else {
                const newHeight = layoutData?.children?.[0]?.box?.height;
                if (!newHeight) return;

                setTimeout(() => {
                  container.remove();
                  resolve(newHeight);
                }, 0);
              }
            } catch (error) {
              container.remove();

              const err = error as Error & { name?: string; message?: string };

              if (
                err?.name === 'BindingError' &&
                err?.message?.includes('Expected null or instance of Config')
              ) {
                console.error('react-pdf BindingError:', err);

                reject(
                  new Error(
                    'Failed to measure PDF height due to BindingError. Please avoid calling measurePdfHeight multiple times in parallel.',
                  ),
                );
              } else {
                reject(err);
              }
            }
          }}
        >
          <Page size='A4'>
            <View style={{ width: '100%' }}>{renderFooterView}</View>
          </Page>
        </Document>
      </PDFViewer>
    );

    import('react-dom/client')
      .then(({ createRoot }) => {
        try {
          const root = createRoot(container);
          root.render(<TempFooterDocument />);
        } catch (err) {
          container.remove();
          reject(err);
        }
      })
      .catch((err) => {
        container.remove();
        reject(err);
      });
  });
};

async function serialMeasure<T>(
  items: T[],
  fn: (item: T, index: number) => Promise<number>,
): Promise<number[]> {
  const result: number[] = [];
  for (let i = 0; i < items.length; i++) {
    result.push(await fn(items[i], i));
  }
  return result;
}

// Helper to convert EdgeInsets to react-pdf style
const edgeInsetsToStyle = (padding?: EdgeInsets) => {
  if (!padding) return {};
  return {
    paddingTop: toPdfUnit(padding.top),
    paddingRight: toPdfUnit(padding.right),
    paddingBottom: toPdfUnit(padding.bottom),
    paddingLeft: toPdfUnit(padding.left),
  };
};

// Helper to convert BorderType to react-pdf style
const borderToStyle = (border?: BorderType) => {
  if (!border) return {};
  const style: PdfBorderStyle = {};
  if (border.width) {
    if (border.width.top !== undefined)
      style.borderTopWidth = toPdfUnit(border.width.top);
    if (border.width.right !== undefined)
      style.borderRightWidth = toPdfUnit(border.width.right);
    if (border.width.bottom !== undefined)
      style.borderBottomWidth = toPdfUnit(border.width.bottom);
    if (border.width.left !== undefined)
      style.borderLeftWidth = toPdfUnit(border.width.left);
  }
  if (border.style) {
    style.borderStyle = border.style as
      | 'solid'
      | 'dashed'
      | 'dotted'
      | undefined;
  }
  if (border.color) {
    style.borderColor = border.color;
  }
  return style;
};

// Helper to convert TypographyType to react-pdf style
const mapFontWeight = (weight?: FontWeightType) => {
  switch (weight) {
    case 'light':
      return 300;
    case 'normal':
      return 400;
    case 'medium':
      return 500;
    case 'bold':
      return 700;
    default:
      return 400;
  }
};

const typographyToStyle = (typography?: TypographyType) => {
  if (!typography) return {};
  return {
    fontFamily: typography.fontFamily,
    fontSize: toPdfUnit(typography.fontSize),
    fontWeight: mapFontWeight(typography.fontWeight),
    textTransform: typography.textTransform,
    color: typography.color,
    textDecoration: typography.textDecoration,
    letterSpacing: toPdfUnit(typography.letterSpacing),
    wordSpacing: toPdfUnit(typography.wordSpacing),
    lineHeight: typography.lineHeight,
    textAlign: typography.textAlign,
    fontStyle: typography.fontStyle,
    backgroundColor: typography?.backgroundColor,
  };
};

// Merge all style helpers
const mergeStyle = (...styles: unknown[]) =>
  Object.assign({}, ...styles.filter(Boolean)) || {};

// Table cell style helper
const sharedCellStyleToStyle = (style?: SharedCellStyle) => {
  return mergeStyle(
    typographyToStyle(style),
    edgeInsetsToStyle(style?.padding),
    borderToStyle(style?.border),
    {
      backgroundColor: style?.backgroundColor,
      width: toPercent(style?.width),
    },
  );
};

// Render Table
const renderTable = (field: TableField, idx: number) => {
  const headerRow = field.rows?.find((r) => r.isHeader);
  const dataRows = field.rows?.filter((r) => !r.isHeader) ?? [];
  const isShowHeader = field?.isShowHeader;
  const isHideLastRowBorder = field?.isHideLastRowBorder;
  const tableGap = field.style?.gap ?? 0;
  return (
    <View
      key={idx}
      style={mergeStyle(
        field.style && {
          width: toPercent(field.style.width),
          backgroundColor: field.style.backgroundColor,
          ...edgeInsetsToStyle(field.style.padding),
          ...borderToStyle(field.style.border),
        },
      )}
      wrap
    >
      {isShowHeader && headerRow && (
        <View
          style={{
            flexDirection: 'row',
            width: '100%',
            ...(tableGap && { gap: toPdfUnit(tableGap) }),
          }}
          wrap
        >
          {headerRow.cells.map((cell: TableCell, cIdx: number) => {
            const mergedStyle: SharedCellStyle = {
              ...field.headersStyle,
              ...field.headerStyles?.[cIdx],
              ...cell.style,
              width: '100%',
            };
            const cellBorder =
              field.headerStyles?.[cIdx]?.border || field.headersStyle?.border;

            const hideBorder = cellBorder?.style === 'none';

            const width =
              field.headerStyles?.[cIdx]?.width || field.headersStyle?.width;

            const isLastCol = cIdx === headerRow.cells.length - 1;
            const cellBorderStyle = {
              borderLeftWidth: isLastCol
                ? 0
                : field.headerStyles?.[cIdx]?.border?.width?.left ||
                  field.headersStyle?.border?.width?.left,
            };

            const mergedCellStyle = mergeStyle(
              sharedCellStyleToStyle(mergedStyle),
              cellBorderStyle,
              {
                flex: cell.colSpan ?? 1,
                maxWidth: toPercent(width),
                ...(hideBorder && { borderWidth: 0 }),
              },
            );

            return (
              <Text key={cIdx} style={mergedCellStyle} wrap>
                {cell.value}
              </Text>
            );
          })}
        </View>
      )}
      {dataRows.map((row, rIdx) => {
        // const totalColSpan = row.cells.reduce((sum, cell) => {
        //   return sum + (cell.colSpan ?? 0);
        // }, 0);

        return (
          <View
            key={rIdx}
            style={{
              flexDirection: 'row',
              width: '100%',
              ...(tableGap && { gap: toPdfUnit(tableGap) }),
            }}
            wrap
          >
            {row.cells.map((cell: TableCell, cIdx: number) => {
              const width =
                field.headerStyles?.[cIdx]?.width || field.columnsStyle?.width;
              const cellBorder =
                field.columnStyles?.[cIdx]?.border ||
                field.columnsStyle?.border;

              const hideBorder = cellBorder?.style === 'none';

              const mergedStyle: SharedCellStyle = {
                ...field.columnsStyle,
                ...field.columnStyles?.[cIdx],
                ...cell.style,
                width: '100%',
              };

              const isLastCol = cIdx === row.cells.length - 1;
              const isLastRow =
                rIdx === dataRows.length - 1 && isHideLastRowBorder;
              const isFirstRow = rIdx === 0;
              const cellBorderStyle = {
                borderLeftWidth: isLastCol
                  ? 0
                  : toPdfUnit(mergedStyle.border?.width?.left),
                borderTopWidth: isFirstRow
                  ? 0
                  : toPdfUnit(mergedStyle?.border?.width?.top),
                borderBottomWidth: isLastRow
                  ? 0
                  : toPdfUnit(mergedStyle?.border?.width?.bottom),
              };

              const input = cell.value;

              const regex = /^\$\{(.+?)\}(.*)$/;
              const match = input.match(regex);

              const identifier = match ? match[1] : undefined;
              const data = match ? match[2] : input;

              const mergedCellStyle = mergeStyle(
                sharedCellStyleToStyle(mergedStyle),
                cellBorderStyle,
                {
                  // flex: cell.colSpan
                  //   ? cell.colSpan
                  //   : totalColSpan > 0
                  //     ? 1 / totalColSpan
                  //     : 1,
                  // flexShrink: 1,
                  // flexBasis: 0,
                  // ...(!cell.colSpan && { maxWidth: toPercent(width) }),

                  flex: cell.colSpan ?? 1,
                  maxWidth: toPercent(width),
                  overflow: 'hidden',
                  ...(identifier === 'b' && { fontWeight: 700 }),
                  ...(hideBorder && { borderWidth: 0 }),
                },
              );

              return (
                <Text key={cIdx} style={mergedCellStyle} wrap>
                  {data}
                </Text>
              );
            })}
          </View>
        );
      })}
    </View>
  );
};

const htmlTagToStyle = (tag: string, attribs: HtmlAttribs = {}) => {
  let style: Style = {};

  const inlineStyles = attribs.style ? parseStyleString(attribs.style) : {};

  switch (tag) {
    case 'strong':
      style.fontWeight = 700;
      break;
    case 'em':
      style.fontStyle = 'italic';
      break;
    case 'u':
      style.textDecoration = 'underline';
      break;
    case 's':
      style.textDecoration = 'line-through';
      break;
    case 'mark':
      if (inlineStyles['background-color']) {
        style.backgroundColor = inlineStyles['background-color'];
      }
      break;
    case 'span':
    case 'p':
    case 'td':
    case 'th':
      if (inlineStyles['font-size']) {
        style.fontSize = toPdfUnit(inlineStyles['font-size']);
      }
      if (inlineStyles['color']) {
        style.color = inlineStyles['color'];
      }
      if (inlineStyles['text-align']) {
        style.textAlign = inlineStyles['text-align'] as
          | 'left'
          | 'center'
          | 'right'
          | 'justify'
          | undefined;
      }
      break;
    default:
      break;
  }

  return style;
};

// Recursive renderer for HTML to react-pdf
const renderHtmlToPdf = (
  node: DOMNode,
  idx: number = 0,
  context: HtmlRenderContext = {},
): JSX.Element | null => {
  if (node.type === 'tag' && node.children?.length === 0 && node.name === 'p') {
    return <Text key={idx}>{'\u00A0'}</Text>;
  }

  if (node.type === 'text') {
    return <Text key={idx}>{node.data}</Text>;
  }

  if (node.type === 'tag') {
    const el = node as Element;
    const { name, attribs = {} } = el;

    const children: DOMNode[] = Array.isArray(el.children)
      ? (el.children as DOMNode[])
      : [];

    const style = htmlTagToStyle(name, attribs);

    const renderChildren = () =>
      children.map((child, i) => renderHtmlToPdf(child, i, context));

    switch (name) {
      case 'table':
        return (
          <View
            key={idx}
            style={[
              {
                width: '100%',
                borderColor: '#000',
              },
              style,
            ]}
            wrap={false}
          >
            {renderChildren()}
          </View>
        );

      case 'colgroup':
      case 'col':
        return null;

      case 'thead':
      case 'tbody':
      case 'tfoot': {
        const rowElements =
          children.filter((c) => (c as Element).name === 'tr') || [];

        return (
          <View key={idx}>
            {rowElements.map((rowNode, rowIndex) =>
              renderHtmlToPdf(rowNode, rowIndex, {
                ...context,
                rowIndex,
                totalRowCount: rowElements.length,
              }),
            )}
          </View>
        );
      }

      case 'tr': {
        const rowIndex = context?.rowIndex ?? 0;
        const totalRowCount = context?.totalRowCount ?? 1;
        const isFirstRow = rowIndex === 0;
        const isLastRow = rowIndex === totalRowCount - 1;

        return (
          <View key={idx} style={{ flexDirection: 'row' }}>
            {children?.map((cellNode, cellIndex) =>
              renderHtmlToPdf(cellNode, cellIndex, {
                ...context,
                rowIndex,
                totalRowCount,
                isFirstRow,
                isLastRow,
                cellIndex,
              }),
            )}
          </View>
        );
      }

      case 'td':
      case 'th': {
        const colspan = parseInt(attribs.colspan || '1', 10);
        const isHeader = name === 'th';
        const { isLastRow = false, cellIndex = 0 } = context || {};

        const siblings =
          node.parent?.children?.filter((n) => n.type === 'tag') || [];
        const isLastCol = cellIndex === siblings.length - 1;
        const isSecondToLastCol = cellIndex === siblings.length - 2;

        return (
          <View
            key={idx}
            style={[
              {
                flex: colspan,
                borderColor: '#bdbdbd',
                borderWidth: 0.75,
                borderBottomWidth: !isLastRow ? 0 : 0.75,
                borderLeftWidth: !isLastCol ? 0.75 : 0,
                borderRightWidth: isLastCol || isSecondToLastCol ? 0.75 : 0,
                paddingVertical: 2.25,
                paddingHorizontal: 3.75,
                minHeight: 15,
                ...(isHeader && {
                  fontWeight: 500,
                  backgroundColor: '#00000014',
                }),
              },
              style,
            ]}
          >
            {renderChildren()}
          </View>
        );
      }

      case 'p':
        return (
          <Text key={idx} style={style}>
            {renderChildren()}
          </Text>
        );

      case 'strong':
      case 'em':
      case 'u':
      case 's':
      case 'span':
      case 'mark':
        return (
          <Text key={idx} style={style}>
            {renderChildren()}
          </Text>
        );

      case 'ol': {
        const newStack = [...(context.listStack ?? []), 1];

        return (
          <View key={idx} style={{ marginLeft: 16 }}>
            {children.map((liNode, i) =>
              renderHtmlToPdf(liNode, i, {
                listStack: [...newStack.slice(0, -1), i + 1],
              }),
            )}
          </View>
        );
      }

      case 'ul': {
        const newStack = [...(context.listStack ?? []), 1];

        return (
          <View key={idx} style={{ marginLeft: 16 }}>
            {children.map((liNode, i) =>
              renderHtmlToPdf(liNode, i, {
                listStack: [...newStack.slice(0, -1), i + 1],
                isUnOrderedList: true,
              }),
            )}
          </View>
        );
      }

      case 'li': {
        const stack = context.listStack ?? [];
        const level = stack.length;
        const index = stack[level - 1];
        const isUnOrderedList = context.isUnOrderedList;

        const bullet = getListMarker(level, index, isUnOrderedList);

        return (
          <View key={idx} style={{ flexDirection: 'row', marginBottom: 2 }}>
            <Text
              style={{
                marginRight: 6,
              }}
            >
              {bullet}
            </Text>
            <View style={{ flex: 1 }}>
              {children.map((child, i) => renderHtmlToPdf(child, i, context))}
            </View>
          </View>
        );
      }

      case 'hr':
        return (
          <View
            key={idx}
            style={{ borderBottomWidth: 0.5, marginVertical: 8 }}
          />
        );

      default:
        return (
          <Text key={idx} style={style}>
            {renderChildren()}
          </Text>
        );
    }
  }

  return null;
};

const renderField = (field: Field, idx: number, hideEmptyContent?: boolean) => {
  switch (field.type) {
    case 'title': {
      if (hideEmptyContent && (!field?.value || field?.value === '-')) return;

      if (field?.id === 'pageSize') {
        return (
          <Text
            key={idx}
            style={mergeStyle(
              {
                fontSize: 10,
              },
              field.style && typographyToStyle(field.style),
              field.style && edgeInsetsToStyle(field.style.padding),
              field.style && { width: toPercent(field.style.width) },
            )}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
            wrap
          />
        );
      }

      if (field?.id === 'renderList') {
        const data = safeJSONParse(field?.value ?? '');

        if (!isBodyContentDetailsArray(data)) return;

        return renderList(data);
      }

      const value = field?.value ?? '';
      const valueWithNewlines = String(value).replace(/\\n/g, '\n');

      return (
        <Text
          key={idx}
          style={mergeStyle(
            {
              fontSize: 10,
            },
            field.style && typographyToStyle(field.style),
            field.style && edgeInsetsToStyle(field.style.padding),
            field.style && { width: toPercent(field.style.width) },
            field.style?.border && { ...borderToStyle(field.style?.border) },
          )}
          wrap
        >
          {valueWithNewlines.split('').map((s, idx) => {
            return <Text key={idx}>{s}</Text>;
          })}
        </Text>
      );
    }
    case 'textarea':
      return (
        <View
          key={idx}
          style={mergeStyle(
            { fontSize: 10 },
            field.style && typographyToStyle(field.style),
            field.style && edgeInsetsToStyle(field.style.padding),
            field.style && { width: toPercent(field.style.width) },
          )}
        >
          {parse(field.value, {
            replace: (domNode) => renderHtmlToPdf(domNode),
          })}
        </View>
      );
    case 'image': {
      return (
        <View
          key={idx}
          style={mergeStyle(
            field.style && {
              minWidth: toPdfUnit(field.style.width),
              minHeight: toPdfUnit(field.style.height),
              ...edgeInsetsToStyle(field.style.padding),
            },
          )}
          wrap={false}
        >
          <Image
            src={field.value || imagePlaceholder}
            style={mergeStyle(
              field.style && {
                width: toPdfUnit(field.style.width),
                height: toPdfUnit(field.style.height),

                objectFit: field.style.objectFit,
                objectPosition: field.style.objectPosition,
              },
            )}
          />
        </View>
      );
    }
    case 'table': {
      if (hideEmptyContent && field?.rows && field.rows?.length <= 1) return;
      return renderTable(field, idx);
    }
    default:
      return null;
  }
};

const renderCell = (cell: Cell, cIdx: number, hideEmptyContent?: boolean) => {
  const width = toPercent(cell?.style?.width);
  return (
    <View
      key={`${cIdx}${hideEmptyContent}`}
      style={mergeStyle(
        {
          ...(!width && { alignSelf: 'flex-start' }), // prevent full-width stretch
        },
        cell.style && {
          ...(!width ? {} : { width }),
          backgroundColor: cell.style.backgroundColor,
          flexDirection: cell.style.flexDirection,
          alignItems: cell.style.alignItems,
          justifyContent: cell.style.justifyContent,
          gap: toPdfUnit(cell?.style?.gap),
          ...edgeInsetsToStyle(cell.style.padding),
          ...borderToStyle(cell.style.border),
          height: '100%',
        },
      )}
      wrap
    >
      {(cell.fields || []).map((field, idx) =>
        renderField(field, idx, hideEmptyContent),
      )}
      {(cell.rows || []).map((row, rIdx) => renderRow(row, rIdx))}
    </View>
  );
};

const renderRow = (row: Row, rIdx: number, hideEmptyContent?: boolean) => {
  return (
    <View
      key={rIdx}
      style={mergeStyle(
        {
          flexDirection: 'row',
        },
        row.style && {
          columnGap: toPdfUnit(row.style?.columnGap),
          width: toPercent(row.style.width),
          backgroundColor: row.style.backgroundColor,
          flexDirection: row.style.flexDirection ?? 'row',
          alignItems: row.style.alignItems,
          justifyContent: row.style.justifyContent,
          ...edgeInsetsToStyle(row.style.padding),
          ...borderToStyle(row.style.border),
        },
      )}
      wrap
    >
      {row.cells.map((cell, cIdx) => renderCell(cell, cIdx, hideEmptyContent))}
    </View>
  );
};

const renderSection = (
  section: Section,
  sIdx: number,
  height?: number,
  wrapStatus: boolean = true,
  hideEmptyContent?: boolean,
) => {
  if (hideEmptyContent && isAllSectionsEmpty([section])) return;

  return (
    <View
      key={sIdx}
      style={[
        mergeStyle(
          section.style && {
            width: toPercent(section.style?.width),
            rowGap: toPdfUnit(section.style.rowGap),
            backgroundColor: section.style.backgroundColor,
            alignItems: section.style.alignItems,
            justifyContent: section.style.justifyContent,
            ...edgeInsetsToStyle(section.style.padding),
            ...borderToStyle(section.style.border),
            ...(!wrapStatus && {
              flex: 1,
              justifyContent: 'flex-end',
            }),
            flexShrink: 0,
          },
        ),
        height !== undefined &&
          typeof height === 'number' && { minHeight: height },
      ]}
      wrap={wrapStatus}
    >
      {section.rows.map((row, rIdx) => renderRow(row, rIdx, hideEmptyContent))}
    </View>
  );
};

const renderHeader = (
  header: HeaderFooterType[] | undefined,
  _staticTotalPages: number,
  heights: number[] = [],
  paddingX: number = 0,
) => {
  return (
    <View
      wrap={false}
      fixed
      style={{
        width: '100%',
        position: 'absolute',
        left: toPdfUnit(paddingX) ?? 0 / 2,
        top: 0,
        flexShrink: 0,
      }}
    >
      {header?.map((section, i) => (
        <View
          key={i}
          render={({}) => {
            const shouldRender = section.showAllPage;
            // (section.firstPageOnly && pageNumber === 1) ||
            // (section.lastPageOnly && pageNumber === staticTotalPages);

            return shouldRender
              ? renderSection(section, -(i + 1), heights[i])
              : null;
          }}
        />
      )) ?? null}
    </View>
  );
};

const renderFooter = (
  footer: HeaderFooterType[] | undefined,
  _staticTotalPages: number,
  heights: number[] = [],
  paddingX: number = 0,
) => {
  return (
    <View
      wrap={false}
      fixed
      style={{
        width: '100%',
        position: 'absolute',
        left: toPdfUnit(paddingX) ?? 0 / 2,
        bottom: 0,
      }}
    >
      {footer?.map((section, i) => (
        <View
          key={i}
          render={({}) => {
            const shouldRender = section.showAllPage;
            // (section.firstPageOnly && pageNumber === 1) ||
            // (section.lastPageOnly && pageNumber === staticTotalPages);

            return shouldRender
              ? renderSection(section, -(i + 1), heights[i])
              : null;
          }}
        />
      )) ?? null}
    </View>
  );
};

export const measureTemplate = async (
  template: TemplateState,
): Promise<TemplateState> => {
  if (!isTemplateState(template)) {
    throw new Error('Invalid template format.');
  }
  const measuredFooterHeights = await serialMeasure(
    template.footer ?? [],
    (footer, i) => measurePdfHeight(renderSection(footer, -(i + 1))),
  );

  const measuredHeaderHeights = await serialMeasure(
    template.header ?? [],
    (header, i) => measurePdfHeight(renderSection(header, -(i + 1))),
  );

  const footerHeight = getShowAllPageHeight(
    template.footer,
    measuredFooterHeights,
  );
  const headerHeight = getShowAllPageHeight(
    template.header,
    measuredHeaderHeights,
  );

  const page = template.pages[0];
  return {
    ...template,
    pages: [
      {
        ...page,
        style: {
          ...page?.style,
          padding: {
            ...page?.style?.padding,
            top: headerHeight / 0.75 + (page?.style?.padding?.top ?? 0),
            bottom: footerHeight / 0.75 + (page?.style?.padding?.top ?? 0),
          },
        },
      },
    ],
  };
};

const PDFDocument: React.FC<{
  template: TemplateState;
  onMeasuringStateChange?: (isMeasuring: boolean) => void;
  measureTemplate?: boolean;
  hideEmptyContent?: boolean;
}> = ({
  template,
  onMeasuringStateChange,
  measureTemplate = true,
  hideEmptyContent,
}) => {
  const [totalPages, setTotalPages] = useState(1);
  const [headerHeights, setHeaderHeights] = useState<number[]>([]);
  const [footerHeights, setFooterHeights] = useState<number[]>([]);

  useEffect(() => {
    if (!measureTemplate) return;

    onMeasuringStateChange?.(true);
    let isMounted = true;
    (async () => {
      const measuredFooterHeights = await serialMeasure(
        template.footer ?? [],
        (footer, i) => measurePdfHeight(renderSection(footer, -(i + 1))),
      );

      const measuredHeaderHeights = await serialMeasure(
        template.header ?? [],
        (header, i) => measurePdfHeight(renderSection(header, -(i + 1))),
      );

      if (isMounted) {
        setFooterHeights(measuredFooterHeights);
        setHeaderHeights(measuredHeaderHeights);
        onMeasuringStateChange?.(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [template, measureTemplate]);

  const isLastPageFooterIndex = template.footer?.findIndex(
    (item) => item.lastPageOnly,
  );

  const footerHeight = getShowAllPageHeight(template.footer, footerHeights);
  const headerHeight = getShowAllPageHeight(template.header, headerHeights);

  return (
    <Document
      onRender={(props) => {
        const total = props['_INTERNAL__LAYOUT__DATA_']?.children?.length;
        if (typeof total === 'number') setTotalPages(total);
      }}
    >
      {(template.pages || []).map((page, pIdx) => {
        return (
          <Page
            key={pIdx}
            size={page.pageSize.name as PageSize}
            style={mergeStyle(
              {
                fontSize: 10,
              },
              page.style && {
                backgroundColor: page.style.backgroundColor,
                alignItems: page.style.alignItems,
                justifyContent: page.style.justifyContent,
                ...edgeInsetsToStyle(page.style.padding),
                ...borderToStyle(page.style.border),
                ...{
                  paddingTop:
                    (toPdfUnit(page?.style?.padding?.top ?? 0) ?? 0) +
                    headerHeight,
                  paddingBottom:
                    (toPdfUnit(page?.style?.padding?.bottom ?? 0) ?? 0) +
                    footerHeight,
                },
              },
              {
                fontFamily: 'Roboto',
                position: 'relative',
              },
            )}
            wrap
            orientation={page.orientation}
          >
            {page?.backgroundImage?.value?.trim() && (
              <Image
                src={page.backgroundImage.value}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: toPdfUnit(page?.style?.padding?.left || 0) ?? 0 / 2,
                  width: '100%',
                  height: '100%',
                  objectFit: page.backgroundImage.style?.objectFit ?? 'contain',
                  objectPosition:
                    page.backgroundImage.style?.objectPosition ?? 'center',
                }}
                fixed
              />
            )}
            {renderHeader(
              template.header,
              totalPages,
              headerHeights,
              page?.style?.padding?.left,
            )}
            {page.sections.map((section, sIdx) =>
              renderSection(
                section,
                sIdx,
                undefined,
                undefined,
                hideEmptyContent,
              ),
            )}

            {isLastPageFooterIndex !== undefined &&
              template.footer &&
              isLastPageFooterIndex !== -1 &&
              renderSection(
                template.footer[isLastPageFooterIndex],
                -(isLastPageFooterIndex + 1),
                (footerHeights[isLastPageFooterIndex ?? -1] ?? 0) * 0.77,
                false,
              )}
            {renderFooter(
              template.footer,
              totalPages,
              footerHeights,
              page?.style?.padding?.left,
            )}
          </Page>
        );
      })}
    </Document>
  );
};

export default PDFDocument;
